import { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { supabase } from './supabase';
import useIdleSession from './hooks/auth/useIdleSession';
import useSessionFullName from './hooks/auth/useSessionFullName';
import {
  markPasswordRecoveryFlowFromUrl,
  noteRecoveryEventUser,
  clearPasswordRecoveryFlowIntent,
} from './utils/auth/passwordRecoverySession';
import { consumeResetPasswordSuccessFlag } from './utils/auth/resetPasswordSuccessFlag';
import { getSessionWithRetry, isSessionLockError } from './utils/auth/authSessionSyncHelpers';
import { createSyncSession } from './utils/auth/syncSession';
import { setSentryContext, clearSentryContext } from './utils/error/sentryContext';
import { setCurrentTenantRole } from './utils/auth/currentTenantRoleStore';
import { clearAllDashboardCache } from './utils/cache/dashboardCache';
import { clearMfaGate } from './utils/auth/mfaGateCache';
import { clearPdfCache } from './utils/invoice/pdfRenderCache';
import { clearEstimatePdfCache } from './utils/estimate/estimatePdfRenderCache';
import { wipeDocumentCache } from './utils/pdf/documentCacheWipe';
import { clearLastRoute } from './utils/ui/lastRouteStorage';
import { clearPerUserDeviceState, clearSignedOutMemoryState } from './utils/auth/clearPerUserDeviceState';
import useLastLoginStamp from './hooks/auth/useLastLoginStamp';
import { useAuthSessionWatches } from './hooks/auth/useAuthSessionWatches';
import {
  clearRevokedFlag,
  isRevokedFlagFresh,
  clearSessionRevokedElsewhereFlag,
  isSessionRevokedElsewhereFlagFresh,
} from './utils/auth/sessionLiveness';
import { coverAppForSignOut, releaseSignOutSplash, needsUnplannedSignOutCover } from './ui/splash/signOutSplash';

import { SessionWarningContext } from './components/auth/sessionWarningContext';
import { runAfterAuthHandoff } from './ui/splash/authHandoffHold';
import { ldLog } from './utils/ui/loginDebug';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [sessionUser, setSessionUser] = useState(null);
  const [role, setRole] = useState(null); // wrapped below as setRoleMirrored (RD-AUD-013)
  const [tenantId, setTenantId] = useState(null);
  const [tenantIsActive, setTenantIsActive] = useState(null);
  const [industryType, setIndustryType] = useState(null);
  const [customIndustryLabel, setCustomIndustryLabel] = useState(null);
  const [tenantName, setTenantName] = useState(null);
  const [tenantLoading, setTenantLoading] = useState(true);
  const [tenantError, setTenantError] = useState(null);
  /** True when the user has an outstanding tenant invite (must complete invite flow; password login alone is insufficient). */
  const [tenantInvitePending, setTenantInvitePending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [recovering, setRecovering] = useState(false);
  const [authNotice, setAuthNotice] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isRootPlatformSuperAdmin, setIsRootPlatformSuperAdmin] = useState(false);
  // Canonical profiles.full_name; fetched once per sign-in (single source of truth).
  const sessionFullName = useSessionFullName(sessionUser?.id);
  /** True while JWT indicates password recovery — no tenant claims (RLS) and UI must not treat as full sign-in. */
  const [sessionIsPasswordRecovery, setSessionIsPasswordRecovery] = useState(false);

  // Mirror auth state into Sentry so production errors are scoped to the right
  // user + tenant + role. Single source of truth across every set/clear path.
  useEffect(() => {
    if (sessionUser) {
      // Telemetry metadata only: waits out a sign-in handoff (authHandoffHold.js).
      // Clear first so a direct A -> B swap never reports B's errors as A.
      clearSentryContext();
      return runAfterAuthHandoff(() => setSentryContext({ user: sessionUser, tenantId, role }));
    }
    clearSentryContext();
    return undefined;
  }, [sessionUser, tenantId, role]);

  const lastSyncedUserIdRef = useRef(null);
  const initialSyncDoneRef = useRef(false);
  /** When true, the next `SIGNED_OUT` is from an explicit logout — not treated as expiry. */
  const userInitiatedSignOutRef = useRef(false);
  /** Timestamp (ms) when the client went offline; null when online. */
  const offlineSinceRef = useRef(null);
  /** True only on a fresh credential sign-in; prevents revocation of other devices on reconnection. */
  const freshSignInRef = useRef(false);
  /** Ref to syncSession for the periodic deactivation check (bridges closure boundary). */
  const syncSessionRef = useRef(null);
  const authEventSeqRef = useRef(0); // orders the deferred unplanned-sign-out teardown vs newer events

  // `notice` (optional) is set BEFORE the teardown so the sign-in card is born with it (KAN-916) — UX only, scope stays 'local'.
  const signOut = useCallback(async (opts) => {
    const notice = opts?.notice; // onClick={signOut} hands in an event: no notice
    userInitiatedSignOutRef.current = true;
    try { await coverAppForSignOut(); } catch { /* UI only — never gates the sign-out (signOutSplash.js) */ }
    setAuthNotice(notice ?? null);
    // No logout audit (dropped with login_success): thin forensic value, and it
    // contended for the auth lock with signOut on slow networks.
    try {
      // RD-SESS-034: THIS device only. auth-js default is scope 'global', which
      // ended the user's sessions on every device. 'local' still calls
      // admin.signOut(accessToken, 'local') server-side (GoTrueClient._signOut),
      // revoking this session's refresh token, then clears local storage.
      const result = await supabase.auth.signOut({ scope: 'local' }).catch((err) => { releaseSignOutSplash(); if (notice) setAuthNotice(null); throw err; });
      // auth-js RESOLVES with { error } on a 5xx and keeps the session: the card never comes,
      // so drop the veil at once (not at the 4 s failsafe) and the notice must not linger (reviews, 2026-10-21).
      if (result?.error) { releaseSignOutSplash(); if (notice) setAuthNotice(null); }
    } finally {
      window.localStorage.removeItem('crewvex-auth');
      // Wipe in-memory dashboard cache so the next user (or re-login of
      // same user) on this browser cannot read another session's data.
      clearAllDashboardCache();
      clearMfaGate(); // ProtectedRoute's MFA gate seed — see mfaGateCache.js
      // Same defense in depth for the cached invoice + estimate PDF bytes (in-memory).
      clearPdfCache();
      clearEstimatePdfCache();
      // R1 (PERF-001 Phase 3): wipe the ON-DISK financial-PDF cache so a shared
      // device never serves a prior user's invoice/estimate PDFs. Fire-and-forget
      // (async, never throws) — the in-memory clears above already cover this tab.
      void wipeDocumentCache();
      clearPerUserDeviceState(); // RD-SEC-013(d): per-user device state (route, MFA hint, audit marker, name cache)
    }
  }, []);

  useEffect(() => {
    const handler = (event) => {
      if (isSessionLockError(event?.reason)) {
        event.preventDefault();
      }
    };
    window.addEventListener('unhandledrejection', handler);
    return () => window.removeEventListener('unhandledrejection', handler);
  }, []);

  const clearAuthNotice = useCallback(() => setAuthNotice(null), []);

  useLastLoginStamp({ sessionUser, tenantId, tenantLoading, loading });

  // RD-AUD-013: mirrors every role change into currentTenantRoleStore.js.
  const setRoleMirrored = useCallback((r) => { setCurrentTenantRole(r); setRole(r); }, []);

  useEffect(() => {
    let active = true;

    // syncSession orchestrator lives in utils/auth/syncSession.js (extracted
    // to keep this file under the 500-line cap). Behavior is unchanged from
    // the previous inline definition — `active` is wired through as a
    // function so the extracted code can gate state updates after cleanup.
    const syncSession = createSyncSession({
      setters: {
        setSessionUser,
        setRecovering,
        setAuthNotice,
        setSessionIsPasswordRecovery,
        setRole: setRoleMirrored,
        setTenantId,
        setTenantIsActive,
        setIndustryType,
        setCustomIndustryLabel,
        setTenantName,
        setTenantError,
        setTenantInvitePending,
        setIsSuperAdmin,
        setIsRootPlatformSuperAdmin,
        setTenantLoading,
        setLoading,
      },
      refs: { lastSyncedUserIdRef, initialSyncDoneRef, offlineSinceRef, freshSignInRef },
      isActive: () => active,
    });

    syncSessionRef.current = syncSession;

    const initAuth = async () => {
      try {
        const { data: { session } } = await getSessionWithRetry();
        // Success flags from invite acceptance or password reset take priority over session-expired.
        // Both flows call signOut() directly which triggers cv_session_expired — clear it when a success flag is present.
        if (!session && sessionStorage.getItem('cv_invite_password_success')) {
          sessionStorage.removeItem('cv_invite_password_success');
          sessionStorage.removeItem('cv_session_expired');
          if (active) {
            setAuthNotice({ type: 'success', key: 'auth.notices.passwordSet' });
          }
        } else if (!session && sessionStorage.getItem('cv_reset_password_success')) {
          const shouldNotify = consumeResetPasswordSuccessFlag();
          if (active && shouldNotify) {
            setAuthNotice({ type: 'success', key: 'auth.notices.passwordReset' });
          }
        } else if (!session && sessionStorage.getItem('cv_session_expired')) {
          sessionStorage.removeItem('cv_session_expired');
          if (active) {
            clearLastRoute();
            setAuthNotice({ type: 'info', key: 'auth.notices.sessionExpired' });
          }
        }
        await syncSession(session);
      } catch (err) {
        console.error('Failed to initialize auth session:', err);
        if (active) {
          lastSyncedUserIdRef.current = null;
          initialSyncDoneRef.current = false;
          setSessionUser(null);
          setRoleMirrored(null);
          setTenantId(null);
          setTenantIsActive(null);
          setIndustryType(null);
          setCustomIndustryLabel(null);
          setTenantName(null);
          setTenantInvitePending(false);
          setIsSuperAdmin(false);
          setIsRootPlatformSuperAdmin(false);
          setTenantError('auth.errors.sessionRecoveryFailed');
          setTenantLoading(false);
          setRecovering(false);
          setAuthNotice({ type: 'error', key: 'auth.notices.recoveryFailed' });
          setLoading(false);
        }
      }
    };

    initAuth();

    // Session: RD-SESS-001 — 8h refresh + inactivity in Supabase dashboard (RD-PRE-002/003); expiry UX + cv_session_expired.
    // RD-SESS-002 — single active session for WORKERS ONLY: a fresh worker sign-in revokes other devices'
    // refresh tokens (workers share field devices). Admin/supervisor/billing are allowed concurrent sessions
    // and are never revoked. The actual revoke is deferred to syncSession — it needs the role, which is not
    // known synchronously at SIGNED_IN. Here we only flag a genuine fresh sign-in via freshSignInRef.
    //
    // RD-SESS-003: multi-tab behavior — Supabase syncs auth across same-browser tabs automatically (localStorage storage events).
    // Tabs share one session token; this subscription receives cross-tab SIGNED_IN / SIGNED_OUT like same-tab events.
    // signOut({ scope: 'others' }) revokes other devices only; it does not sign out sibling tabs in this browser.

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      ldLog('auth', { ev: event, user: Boolean(session?.user) });
      if (event === 'PASSWORD_RECOVERY') {
        markPasswordRecoveryFlowFromUrl();
        noteRecoveryEventUser(session?.user?.id);
      }
      if (event === 'SIGNED_IN') {
        sessionStorage.removeItem('cv_session_expired');
        // Flag a genuine fresh credential sign-in for the deferred RD-SESS-002
        // worker revoke (executed in syncSession once the role is known). Gate:
        //   - user ID differs from the last synced user — reconnections after
        //     being offline reuse the same ID and must NOT revoke other devices;
        //   - not on /accept-invite (invited users have no prior sessions and the
        //     revoke would race updateUser) or /reset-password (recovery session).
        if (
          session?.user &&
          lastSyncedUserIdRef.current !== session.user.id &&
          window.location.pathname !== '/accept-invite' &&
          window.location.pathname !== '/reset-password'
        ) {
          freshSignInRef.current = true;
          // Dedicated signal for the MFA enrollment baseline (next-login
          // enforcement): the gate snapshots the effective required-role set on
          // this fresh sign-in so a later-added mandate only forces at the NEXT
          // login. Separate from freshSignInRef (consumed+reset by syncSession's
          // worker revoke) so the two never race. Cleared by the gate after it
          // captures the baseline.
          try { window.sessionStorage.setItem('cv_mfa_fresh_login', '1'); } catch (_) { /* private mode */ }
        }
      }
      if (event === 'SIGNED_OUT') {
        clearPasswordRecoveryFlowIntent();
        clearMfaGate(); // expiry / revoke / cross-tab never reach signOut() above
        clearSignedOutMemoryState(); // PII name cache + MFA caches — these paths skip signOut()
        if (userInitiatedSignOutRef.current) {
          userInitiatedSignOutRef.current = false;
          sessionStorage.removeItem('cv_session_expired');
          clearRevokedFlag();
          clearSessionRevokedElsewhereFlag();
        } else if (sessionStorage.getItem('cv_invite_password_success')) {
          sessionStorage.removeItem('cv_invite_password_success');
          sessionStorage.removeItem('cv_session_expired');
          clearRevokedFlag();
          clearSessionRevokedElsewhereFlag();
          setAuthNotice({ type: 'success', key: 'auth.notices.passwordSet' });
        } else if (sessionStorage.getItem('cv_reset_password_success')) {
          const shouldNotify = consumeResetPasswordSuccessFlag();
          clearRevokedFlag();
          clearSessionRevokedElsewhereFlag();
          if (shouldNotify) {
            setAuthNotice({ type: 'success', key: 'auth.notices.passwordReset' });
          }
        } else if (isSessionRevokedElsewhereFlagFresh()) {
          clearSessionRevokedElsewhereFlag();
          clearRevokedFlag();
          sessionStorage.removeItem('cv_session_expired');
          clearLastRoute();
          setAuthNotice({ type: 'error', key: 'auth.notices.sessionRevokedElsewhere' });
        } else if (isRevokedFlagFresh()) {
          // Admin-revoked session (or a liveness probe that just confirmed
          // death) — show a specific message so the user understands it
          // wasn't a normal expiry. Freshness-gated (not a plain presence
          // check): the flag is set for the duration of a probe round-trip
          // (up to ~20s), so a stale leftover flag must never mislabel an
          // unrelated, later sign-out (e.g. idle timeout) as "revoked".
          clearRevokedFlag();
          clearSessionRevokedElsewhereFlag();
          sessionStorage.removeItem('cv_session_expired');
          clearLastRoute();
          setAuthNotice({ type: 'error', key: 'auth.errors.accessRevoked' });
        } else {
          // Defensive: a stale (>30s) revoked flag can land here — clear it for any later SIGNED_OUT.
          clearRevokedFlag();
          clearSessionRevokedElsewhereFlag();
          sessionStorage.setItem('cv_session_expired', '1');
          clearLastRoute();
          setAuthNotice({ type: 'info', key: 'auth.notices.sessionExpired' });
        }
      }
      // Unplanned sign-out in the app: splash first, teardown under it — unless a newer event lands meanwhile.
      const seq = ++authEventSeqRef.current;
      if (event === 'SIGNED_OUT' && needsUnplannedSignOutCover()) void coverAppForSignOut().then(() => { if (seq === authEventSeqRef.current) void syncSession(session); });
      else void syncSession(session);
    });

    const handleOnline = () => {
      if (offlineSinceRef.current === null) return;
      offlineSinceRef.current = null;

      // ASK THE SERVER. Never infer a dead session from time spent offline.
      //
      // This used to branch on `offlineMs >= OFFLINE_GRACE_MS` (1h): past that,
      // it nulled every piece of session state and showed "sessionExpired"
      // WITHOUT any server check — and without even calling signOut(), so the
      // still-valid token stayed in storage while the user was shown a login
      // screen. A false "Please sign in again", manufactured entirely client-side.
      //
      // The numbers show why it was wrong: the grace was 1h, but a session is
      // genuinely valid for 8h of inactivity / a 24h cap (sessionPolicy
      // SERVER_SESSION). So a laptop asleep over lunch, or a phone in a dead
      // zone, was enough to trigger it. Being offline is not evidence that a
      // session died — and we are, by definition, back ONLINE in this handler,
      // so the authoritative answer costs exactly one round trip.
      //
      // Both former branches now do the same correct thing:
      //   • session alive -> syncSession() restores it, user keeps working
      //   • session dead  -> getSession() yields null and syncSession(null)
      //                      drives the normal, honest sign-out path
      //   • call failed   -> swallowed. Fail SAFE (stay signed in) rather than
      //                      inventing a logout; a reconnect is exactly when a
      //                      request is most likely to flake.
      supabase.auth.getSession()
        .then(({ data: { session } }) => { if (active) void syncSession(session); })
        .catch(() => {});
    };

    const handleOffline = () => {
      if (active) {
        offlineSinceRef.current = offlineSinceRef.current ?? Date.now();
        setAuthNotice({ type: 'info', key: 'auth.notices.connectionLost' });
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      active = false;
      subscription.unsubscribe();
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [setRoleMirrored]);

  const sessionUserId = sessionUser?.id;
  useAuthSessionWatches({
    sessionUserId, tenantId, loading, tenantLoading, role, offlineSinceRef, syncSessionRef, signOut, setAuthNotice,
  });

  // Idle clock + "Still there?" warning + inactivity sign-out (useIdleSession).
  // Separate context from authValue: toggling the warning must not re-render
  // every useAuth() consumer.
  const sessionWarningValue = useIdleSession({
    enabled: Boolean(sessionUser && !loading && !tenantLoading),
    role, isSuperAdmin, signOut, setAuthNotice,
  });

  // Memoized so the context value keeps a stable identity across re-renders
  // that don't touch these fields (e.g. the local showSessionWarning toggle).
  // Without this, every auth-provider re-render handed every useAuth() consumer
  // a fresh object and forced a re-render — the highest-blast-radius churn in
  // the app. signOut and clearAuthNotice are useCallback-stable.
  const authValue = useMemo(() => ({
    sessionUser,
    sessionFullName,
    sessionIsPasswordRecovery,
    role,
    isSuperAdmin,
    isRootPlatformSuperAdmin,
    tenantId,
    tenantIsActive,
    industryType,
    customIndustryLabel,
    tenantName,
    tenantLoading,
    tenantError,
    tenantInvitePending,
    loading,
    recovering,
    authNotice,
    clearAuthNotice,
    signOut,
    setAuthNotice,
  }), [
    sessionUser, sessionFullName, sessionIsPasswordRecovery, role, isSuperAdmin,
    isRootPlatformSuperAdmin, tenantId, tenantIsActive, industryType,
    customIndustryLabel, tenantName, tenantLoading, tenantError,
    tenantInvitePending, loading, recovering, authNotice, clearAuthNotice, signOut, setAuthNotice,
  ]);

  return (
    <AuthContext.Provider value={authValue}>
      {/* The idle warning is NOT rendered here: AuthProvider sits ABOVE
          LocaleProvider (App.js), so the dialog would have no intl ancestor and
          blank the app. `SessionTimeoutWarningHost` renders it inside
          LocaleProvider from this context. */}
      <SessionWarningContext.Provider value={sessionWarningValue}>
        {children}
      </SessionWarningContext.Provider>
    </AuthContext.Provider>
  );
}

/** Live session's email — null outside AuthProvider (isolated renders). No fetch. */
export function useSessionEmail() {
  const ctx = useContext(AuthContext);
  return ctx?.sessionUser?.email ?? null;
}

/** Account menu's role icon (RD-UI-031): tenant role + platform flags — null-safe outside AuthProvider. No fetch. */
export function useSessionRole() {
  const ctx = useContext(AuthContext);
  return {
    role: ctx?.role ?? null,
    isSuperAdmin: ctx?.isSuperAdmin === true,
    isRoot: ctx?.isRootPlatformSuperAdmin === true,
  };
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
